import React from 'react';
import styled, { css } from 'styled-components';
import { ObjectFit } from '@westwing/ui-kit/Picture/Image';
import { InlineSpinner } from '@westwing/ui-kit/Spinner';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import ZoomInIcon from './assets/icons/zoomin.svg';
import ZoomOutIcon from './assets/icons/zoomout.svg';
import { MediaImage, Orientation } from '@ids/components/Media/MediaImage';
import { ProjectSlide } from '@ids/types/now-api';

const ImageWrapper = styled.div<{ $zoomOut: boolean }>`
    width: 100%;
    height: 100%;

    picture {
        display: initial;
    }

    ${p =>
        p.$zoomOut
            ? css`
                  cursor: url(${ZoomOutIcon}) 20 10, zoom-out;
              `
            : css`
                  cursor: url(${ZoomInIcon}) 20 10, zoom-in;
              `}
`;

export type ZoomImageProps = {
    image: ProjectSlide['image'];
    onError: () => void;
    currentIndex: number;
};

const StyledImage = styled(MediaImage)<{ $orientation: Orientation }>`
    display: block;
    object-fit: contain;

    ${({ $orientation }) => {
        if ($orientation === 'LANDSCAPE') {
            return css`
                width: 100%;
                height: auto;
            `;
        }
        return css`
            width: auto;
            height: 100%;
        `;
    }}
`;

export const ZoomImage = ({ image, onError, currentIndex }: ZoomImageProps) => {
    const [zoomDirection, setZoomDirection] = React.useState<'in' | 'out'>('in');

    const onClick =
        ({ zoomIn, zoomOut, state }: ReactZoomPanPinchRef) =>
        () => {
            if (zoomDirection === 'out') {
                zoomOut();
            } else {
                zoomIn();
            }

            if (state.scale > 2.5) {
                setZoomDirection('out');
            }
            if (state.scale < 1.5) {
                setZoomDirection('in');
            }
        };

    const [isImageLoading, setIsImageLoading] = React.useState(true);

    return (
        <TransformWrapper maxScale={3}>
            {zoomRef => (
                <TransformComponent
                    contentStyle={{ width: '100%', height: '100%' }}
                    wrapperStyle={{ width: '100%', height: '100%' }}
                >
                    {isImageLoading && <InlineSpinner style={{ position: 'relative' }} />}
                    <ImageWrapper $zoomOut={zoomDirection === 'out'} onClick={onClick(zoomRef)}>
                        <StyledImage
                            data-testid={`zoom-image-${currentIndex}`}
                            sizes="100vw"
                            image={image}
                            onLoad={() => setIsImageLoading(false)}
                            onError={onError}
                            objectFit={ObjectFit.CONTAIN}
                            $orientation={image.orientation}
                        />
                    </ImageWrapper>
                </TransformComponent>
            )}
        </TransformWrapper>
    );
};
